/**
 * ORGANISATION_SCHEMA
 *
 * Complete Organization schema for SMARTY brand.
 * Placed on: Homepage (canonical URL)
 *
 */
export const jsonLdOrganization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'SMARTY',
  url: 'https://smarty.co.uk',
  logo: {
    '@type': 'ImageObject',
    url: 'https://smarty.co.uk/assets/images/smarty_logo.png',
  },
  description:
    'SMARTY is a new SIM-only mobile network that’s built to be fair, transparent and smart.',
  sameAs: [
    'https://x.com/SMARTYMobileUK',
    'https://www.facebook.com/SMARTYMobileuk',
    'https://www.instagram.com/smartymobileuk/',
    'https://www.linkedin.com/company/smartymobile-uk/',
    'https://www.youtube.com/@SMARTYMobileUK',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer support',
    url: 'https://smarty.co.uk/contact',
    availableLanguage: ['en'],
  },
}

/**
 * createFAQPageSchema
 *
 * Factory function to generate a complete FAQPage schema.
 * Use this ONLY when rendering FAQ content on a page.
 *
 */
export const createFAQPageSchema = (
  faqItems: {
    question: string
    answer: string
  }[]
) => {
  const validItems = faqItems.filter(
    (item) =>
      typeof item.question === 'string' &&
      item.question.trim().length > 0 &&
      typeof item.answer === 'string' &&
      item.answer.trim().length > 0
  )

  if (!validItems.length) {
    return null
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

type FaqSchemaSourceItem = {
  __typename?: string | null
  faqTitle?: string | null
  faqContent?: {
    internal?: {
      content?: string | null
    } | null
  } | null
} | null

export const mapFaqItemsToSchemaItems = (
  faqItems: readonly FaqSchemaSourceItem[]
) => {
  return faqItems
    .filter((item) => item?.__typename === 'ContentfulFaq')
    .map((item) => ({
      question: item?.faqTitle?.trim() || '',
      answer: item?.faqContent?.internal?.content || '',
    }))
    .filter((item) => item.question && item.answer)
}

type AwardSchemaSourceItem = {
  __typename?: string | null
  title?: string | null
  description?: {
    internal?: {
      content?: string | null
    } | null
  } | null
  image?: {
    description?: string | null
  } | null
} | null

export const mapAwardsItemsToSchemaAwards = (
  awardItems: readonly AwardSchemaSourceItem[]
) => {
  const normalizedAwards = awardItems
    .filter((item) => item?.__typename === 'ContentfulGenericBanner')
    .map(
      (item) =>
        item?.description?.internal?.content?.trim() ||
        item?.image?.description?.trim() ||
        item?.title?.trim() ||
        ''
    )
    .filter((award) => award.length > 0)

  return Array.from(new Set(normalizedAwards))
}
